import style from "./projects-cell-new.module.css";
import { useEffect, useRef, useState } from "react";
import Modal from "frontend/modal/modal";
import { useHover, useOnClickOutside } from "usehooks-ts";
import { Project } from "shared/datamodel/schemas/project";
import LeaveCollaborationModalNew from "frontend/modals/collaboration/leave-collaboration-modal-new";
import DeleteCollaborationModalNew from "frontend/modals/collaboration/delete-collaboration-modal-new";
import classNames from "classnames";
import { CollaborationType, InviteMode } from "state-atoms/teams-atoms";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import InviteModalNew from "frontend/modals/invite-modal-new";
import ManageCollaborationMembers from "frontend/modals/collaboration/manage-collaboration-members";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { Team } from "shared/datamodel/schemas/team";
import { ProjectIcon } from "frontend/icons/project";
import { MenuHorizontalIcon } from "frontend/icons/menu-horizontal";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { CanvasTemplate } from "shared/datamodel/schemas";
import InstanceMenu from "./instance-menu";
import RenameModal from "frontend/modals/collaboration/rename-modal";

export function ProjectCellNew({
  parentTeam,
  project,
  isSelected,
  onClick,
  onEditSeats,
  createNewBoard,
  onDeleteSelectedProject,
}: {
  parentTeam: Team;
  project: Project;
  isSelected: boolean;
  onClick: () => void;
  onEditSeats: () => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onDeleteSelectedProject: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [projectName, setProjectName] = useState(project.name);
  const [showInviteProjectMembersModal, setShowInviteProjectMembersModal] = useState(false);
  const [showManageProjectModal, setShowManageProjectModal] = useState(false);
  const [showLeaveCollaborationModal, setShowLeaveCollaborationModal] = useState(false);
  const [showDeleteCollaborationModal, setShowDeleteCollaborationModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const { updateProject: handleEditProjectName } = useProjectsCache();

  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const ref = useRef<HTMLDivElement | null>(null);
  const hover = useHover(ref);
  const hoverMenu = useHover(menuButtonRef);

  useEffect(() => {
    setProjectName(project.name);
  }, [project.name]);

  useOnClickOutside(menuRef, () => {
    setShowMenu(false);
  });

  function menuClicked(event: React.MouseEvent) {
    event.stopPropagation();
    setShowMenu((show) => !show);
  }

  function renameClicked(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setShowMenu(false);
    setShowRenameModal(true);
  }

  const renderProjectMenu = () => {
    return (
      <InstanceMenu
        menuButtonRef={menuButtonRef}
        menuRef={menuRef}
        setShowMenu={setShowMenu}
        project={project}
        team={parentTeam}
        setShowInviteModal={setShowInviteProjectMembersModal}
        setShowMembersModal={setShowManageProjectModal}
        createNewBoard={createNewBoard}
        renameClicked={renameClicked}
        setShowLeaveModal={setShowLeaveCollaborationModal}
        setShowDeleteModal={setShowDeleteCollaborationModal}
      />
    );
  };

  return (
    <>
      <div
        key={project.documentId}
        ref={ref}
        className={classNames(style.cell, { [style.selectedProject]: isSelected })}
        onClick={onClick}
      >
        {hover && projectName.length > 16 && (
          <Tooltip
            side="right"
            label={projectName}
            relativeTo={ref}
            customStyle={{
              backgroundColor: "#DEE1EB",
              color: " #113357",
              fontSize: 12,
              fontWeight: 400,
              borderRadius: 4,
              maxWidth: "500px",
              wordBreak: "break-word",
            }}
            withArrow={true}
          />
        )}
        <div data-testid={projectName} className={style.projectNameContainer}>
          <ProjectIcon color="#AFB3BF" />
          <div className={style.projectName}>{projectName}</div>
        </div>
        {hoverMenu && (
          <Tooltip
            side="top"
            label={"Settings"}
            relativeTo={menuButtonRef}
            customStyle={{
              backgroundColor: "#113357",
              borderRadius: 6,
              color: " #FFF",
              fontSize: 12,
              fontWeight: 300,
            }}
          />
        )}
        {(hover || showMenu) && (
          <div
            data-testid="projectmenuicon"
            onClick={(e) => menuClicked(e)}
            ref={menuButtonRef}
            className={classNames(style.menuIcon, { [style.selectedIcon]: isSelected })}
          >
            <MenuHorizontalIcon />
          </div>
        )}
      </div>
      {showMenu && renderProjectMenu()}
      {showInviteProjectMembersModal && project && parentTeam && (
        <Modal dimBackground={true}>
          <InviteModalNew
            onDismiss={() => {
              setShowInviteProjectMembersModal(false);
            }}
            inviteMode={InviteMode.Edit}
            onEditSeats={onEditSeats}
            source="edit-project-members"
            instance={project}
            type={InstanceType.Project}
            parentInstance={parentTeam as Team}
          />
        </Modal>
      )}
      {showManageProjectModal && project && (
        <Modal dimBackground={true} onClickBackground={() => setShowManageProjectModal(false)}>
          <ManageCollaborationMembers
            instanceId={project.documentId}
            type={InstanceType.Project}
            onDismiss={() => {
              setShowManageProjectModal(false);
            }}
            onEditSeats={onEditSeats}
          />
        </Modal>
      )}
      {showLeaveCollaborationModal && project && (
        <Modal dimBackground={true}>
          <LeaveCollaborationModalNew
            type={CollaborationType.Project}
            onDismiss={() => setShowLeaveCollaborationModal(false)}
            project={project}
            subtitle={`You will lose access to "${project.name}" project and its canvases.`}
          />
        </Modal>
      )}
      {showDeleteCollaborationModal && project && (
        <Modal dimBackground={true}>
          <DeleteCollaborationModalNew
            type={CollaborationType.Project}
            onDismiss={() => setShowDeleteCollaborationModal(false)}
            project={project}
            subtitle={`All canvases within this project will be permanently deleted. You can move canvases to another project or export them before deleting.`}
            isSelected={isSelected}
            onDeleteSelectedCollaboration={onDeleteSelectedProject}
          />
        </Modal>
      )}
      {showRenameModal && (
        <Modal dimBackground={true}>
          <RenameModal
            currentName={projectName}
            onDismiss={() => setShowRenameModal(false)}
            type={InstanceType.Project}
            onEditName={(newName: string) => {
              setProjectName(newName);
              handleEditProjectName({ ...project, name: newName });
            }}
          />
        </Modal>
      )}
    </>
  );
}
